/**
 * Unit 1.4 · IMPL.C: per-row kebab for the clients table.
 *
 * Bead: insurescope-11zkc.2.4.3.3. Mirrors the shared `RowActionsMenu`
 * shell's chrome (ghost icon trigger + stopPropagation + auto-separator
 * before the destructive item) but composes the primitives directly: the
 * "Open Client" item must be a real `<Link>` so ⌘/middle-click open-in-new-
 * tab works, which the shell's onSelect-only `RowAction` shape cannot
 * express. Owns its own Edit/Delete dialog state (self-contained row unit;
 * the page stays thin).
 */
import { ExternalLink, MoreVertical, Pencil, Trash2 } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router";

import type { IClient } from "@/api";
import { buttonVariants } from "@/components/ui/button";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";

import { ClientFormDialog } from "./ClientFormDialog";
import { DeleteClientDialog } from "./DeleteClientDialog";

export function ClientRowActions({ client }: { client: IClient }) {
    const [editOpen, setEditOpen] = useState(false);
    const [deleteOpen, setDeleteOpen] = useState(false);

    return (
        <>
            <DropdownMenu>
                <DropdownMenuTrigger
                    aria-label="Open actions menu"
                    onClick={(e) => e.stopPropagation()}
                    className={cn(
                        buttonVariants({ variant: "ghost", size: "icon-sm" })
                    )}
                >
                    <MoreVertical size={16} />
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="min-w-36">
                    <DropdownMenuItem
                        render={
                            <Link
                                to={`/organizations/clients/${client.id}/library`}
                            />
                        }
                    >
                        <ExternalLink className="size-4" />
                        Open Client
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => setEditOpen(true)}>
                        <Pencil className="size-4" />
                        Edit
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                        variant="destructive"
                        onClick={() => setDeleteOpen(true)}
                    >
                        <Trash2 className="size-4" />
                        Delete
                    </DropdownMenuItem>
                </DropdownMenuContent>
            </DropdownMenu>

            <ClientFormDialog
                open={editOpen}
                onOpenChange={setEditOpen}
                client={client}
            />
            <DeleteClientDialog
                open={deleteOpen}
                onOpenChange={setDeleteOpen}
                client={client}
            />
        </>
    );
}
